import { formatFileSize } from '../compress/compressImageEngine';
import { encodeCanvas } from '../shared/imageOutputFormats';

export { formatFileSize };

export const MAX_FILE_BYTES = 50 * 1024 * 1024;
export const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif';

export const RECOLOR_MODES = ['smart', 'flat', 'replace'];
export const COLOR_PRESETS = ['#E68A00', '#228BE6', '#40C057', '#E03131', '#7950F2', '#000000', '#ffffff'];

const SUPPORTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const SUPPORTED_EXT = /\.(jpe?g|png|webp|gif)$/i;

export function isSupportedLogoColorFile(file) {
    if (!file) return false;
    if (file.type) return SUPPORTED_TYPES.has(file.type);
    return SUPPORTED_EXT.test(file.name || '');
}

export function isValidHexColor(hex) {
    const value = String(hex || '').trim();
    return /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.test(value);
}

export function hexToRgb(hex) {
    let value = String(hex || '#000000').trim().replace('#', '');
    if (/^[0-9a-f]{3}$/i.test(value)) {
        value = value.split('').map((c) => c + c).join('');
    }
    if (!/^[0-9a-f]{6}$/i.test(value)) {
        return [0, 0, 0];
    }
    return [
        parseInt(value.slice(0, 2), 16),
        parseInt(value.slice(2, 4), 16),
        parseInt(value.slice(4, 6), 16),
    ];
}

function outputName(file, ext = 'png') {
    const base = file.name.replace(/\.[^.]+$/, '');
    return `${base}-recolored.${ext}`;
}

async function loadImageElement(file) {
    const url = URL.createObjectURL(file);
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

function colorDistance(r1, g1, b1, r2, g2, b2) {
    return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}

function applyRecolor(r, g, b, a, targetRgb, mode, fromRgb, tolerance) {
    if (a < 8) return [r, g, b, a];

    if (mode === 'replace') {
        if (colorDistance(r, g, b, fromRgb[0], fromRgb[1], fromRgb[2]) <= tolerance) {
            return [...targetRgb, a];
        }
        return [r, g, b, a];
    }

    if (mode === 'flat') {
        return [...targetRgb, a];
    }

    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return [
        Math.round(targetRgb[0] * lum),
        Math.round(targetRgb[1] * lum),
        Math.round(targetRgb[2] * lum),
        a,
    ];
}

export function cloneImageData(imageData) {
    return new ImageData(
        new Uint8ClampedArray(imageData.data),
        imageData.width,
        imageData.height,
    );
}

/** Recolor image pixels in place. */
export function recolorImageData(imageData, options) {
    const {
        mode = 'smart',
        newColor = '#E68A00',
        fromColor = '#000000',
        tolerance = 48,
    } = options;

    if (!isValidHexColor(newColor)) return false;
    if (mode === 'replace' && !isValidHexColor(fromColor)) return false;

    const { data } = imageData;
    const targetRgb = hexToRgb(newColor);
    const fromRgb = hexToRgb(fromColor);

    for (let i = 0; i < data.length; i += 4) {
        const next = applyRecolor(
            data[i],
            data[i + 1],
            data[i + 2],
            data[i + 3],
            targetRgb,
            mode,
            fromRgb,
            tolerance,
        );
        data[i] = next[0];
        data[i + 1] = next[1];
        data[i + 2] = next[2];
        data[i + 3] = next[3];
    }

    return true;
}

export async function loadLogoImageData(file) {
    const img = await loadImageElement(file);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    return {
        imageData: ctx.getImageData(0, 0, canvas.width, canvas.height),
        width: canvas.width,
        height: canvas.height,
    };
}

export function imageDataToPreviewUrl(imageData) {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    canvas.getContext('2d').putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
}

export async function buildRecolorResult(file, imageData, originalSize, format = 'png') {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    canvas.getContext('2d').putImageData(imageData, 0, 0);

    const { blob, ext } = await encodeCanvas(canvas, format);

    return {
        name: outputName(file, ext),
        blob,
        originalSize,
        outputSize: blob.size,
        width: imageData.width,
        height: imageData.height,
    };
}

/**
 * Recolor a logo image while preserving transparency.
 */
export async function recolorLogoFile(file, options) {
    const { imageData } = await loadLogoImageData(file);
    const recolored = cloneImageData(imageData);
    recolorImageData(recolored, options);
    return buildRecolorResult(file, recolored, file.size);
}
